import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';

import { AuthProvider, AuthCheckLogin, AuthDataProvider } from 'neon-auth0';
import { config } from './config';
import {
    LoggerProvider,
    sessionId,
} from './components/providers/logger/Logger';
import Auth0ErrorCallback from './Auth0Callback';
import { UserProvider } from './components/providers/UserProvider';
import { NotificationProvider } from './components/providers/NotificationProvider';
import injectSheet from 'react-jss';
import { AuthorizedApolloProvider } from './components/providers/AuthorizedApolloProvider';
import { BreakpointsProvider } from './components/providers/BreakpointsProvider';
import { BrowserRouter } from 'react-router-dom';
import packageJson from '../package.json';
import { addLoginEntry } from './components/providers/logger/logger-impl';
import { RoutesHistoryProvider } from './components/providers/RoutesHistoryProvider';
import { UnsubscribePage } from './components/containers/client/UnsubscribePage';
import TagManager from 'react-gtm-module';
import { MdsThemeProvider } from './components/providers/MdsThemeProvider';

import App from './App';
import Loader from 'components/ui/LoadingIndicator';
const { version } = packageJson;

TagManager.initialize({
    gtmId: 'GTM-PVVQQ7F',
});

// If this cookie is present then the auth0-spa library will attempt a silent login
// If this cookie is not present, then the user starts in an unauthenticated state
// and will be redirected to the dashboard to log in
document.cookie = 'auth0.is.authenticated=true;path=/;max-age=86400';

if (config.nodeEnv !== 'production') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const whyDidYouRender = require('@welldone-software/why-did-you-render');
    whyDidYouRender(React);
}

declare global {
    interface Window {
        _env: any;
    }
}

const onRedirectCallback = (appState: any): void => {
    window.history.replaceState(
        {},
        document.title,
        appState && appState.targetUrl ? appState.targetUrl : config.basePath
    );
};

const style = {
    '@global': {
        body: {
            // overflowY: 'auto !important' // disable it for now. Maybe later we'll enable it again
        },
        '.medium-zoom-overlay': {
            zIndex: 9999,
        },
        '.medium-zoom-image--opened': {
            zIndex: 9999,
        },
    },
};

export const redirectToUrl = () => {
    if (!config.loginPageUrl) {
        return;
    }
    const redirectUrl = new URL(config.loginPageUrl);
    redirectUrl.searchParams.append(
        'redirect',
        window.location.origin + window.location.pathname + '?sid=' + sessionId
    );
    return redirectUrl;
};

const scopes =
    'insights:content-reader insights:content-edit insights:content-publish admin:insights insights:content-reader-trial';

const appName = 'NeonResearch';

const appInfo = {
    appName: appName,
    appVersion: version,
    sessionId: sessionId,
};

const isUnsubscribe = window.location.href.indexOf('unsubscribe?data') > -1;
const devEnvironmentTags = ['development', 'developmentServer'];
const loginUri = devEnvironmentTags.includes(config.nodeEnv)
    ? undefined
    : redirectToUrl()?.href;

const Index = () => (
    <MdsThemeProvider>
        {isUnsubscribe ? (
            <BrowserRouter>
                <UnsubscribePage />
            </BrowserRouter>
        ) : (
            <Suspense fallback={<Loader />}>
                <AuthProvider
                    domain={config.auth0.domain}
                    clientId={config.auth0.clientId}
                    redirectUri={config.auth0.redirectUri}
                    returnToUri={config.auth0.returnToUri}
                    audience={config.auth0.audience}
                    onRedirectCallback={onRedirectCallback}
                    scope={scopes}
                >
                    <LoggerProvider appName={appName}>
                        {/* In local development, let Auth0 handle localhost consent/callback directly. */}
                        <AuthCheckLogin
                            loginUri={loginUri}
                            loader={<Loader />}
                            errorComponentCallback={Auth0ErrorCallback}
                            requiredScopes={scopes}
                            appInfo={appInfo}
                            addLogEntry={(param) => {
                                addLoginEntry(param);
                            }}
                            invalidScopeMessage="User is not permissioned to use the Insights system"
                        >
                            <AuthDataProvider>
                                <UserProvider>
                                    <AuthorizedApolloProvider>
                                        <NotificationProvider>
                                            <BreakpointsProvider>
                                                <BrowserRouter
                                                    basename={config.basePath}
                                                >
                                                    <RoutesHistoryProvider>
                                                        <App />
                                                    </RoutesHistoryProvider>
                                                </BrowserRouter>
                                            </BreakpointsProvider>
                                        </NotificationProvider>
                                    </AuthorizedApolloProvider>
                                </UserProvider>
                            </AuthDataProvider>
                        </AuthCheckLogin>
                    </LoggerProvider>
                </AuthProvider>
            </Suspense>
        )}
    </MdsThemeProvider>
);

const Idx = injectSheet(style as any)(Index);
const rootElement = document.getElementById('root');

if (rootElement) {
    createRoot(rootElement).render(<Idx />);
}
